<template>
  <v-container class="pa-2 page-layout" fluid>
    <v-row dense>
      <v-col class="py-0">
        <h1 class="page-title">Provider Documents</h1>
      </v-col>
    </v-row>

    <!-- Estatísticas -->
    <v-row class="mb-4">
      <v-col cols="12" md="4">
        <v-card>
          <v-card-text class="text-center">
            <div class="text-h4">{{ stats.totalDocuments }}</div>
            <div class="text-subtitle-1">Total Documents</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-card>
          <v-card-text class="text-center">
            <div class="text-h4">{{ formatBytes(stats.totalSize) }}</div>
            <div class="text-subtitle-1">Total Size</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-card>
          <v-card-text class="text-center">
            <div class="text-h4">{{ stats.totalDownloads }}</div>
            <div class="text-subtitle-1">Total Downloads</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Botões Upload e Logs (apenas Admin/Superuser/Manager) -->
    <v-row v-if="isAdminOrSuperuserOrManager">
      <v-col>
        <v-btn color="primary" @click="openUploadDialog">
          <v-icon left>mdi-upload</v-icon>
          Upload Document
        </v-btn>
        <v-btn class="ml-2" color="secondary" @click="openAccessHistoryDialog">
          <v-icon left>mdi-account-clock</v-icon>
          Access history
        </v-btn>
      </v-col>
    </v-row>

    <!-- Tabela de Documentos -->
    <v-row>
      <v-col>
        <v-card>
          <v-card-title>
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Search"
              single-line
              hide-details
            ></v-text-field>
          </v-card-title>
          <v-data-table
            :headers="headers"
            :items="documents"
            :search="search"
            :loading="loading"
            class="elevation-1"
          >
            <template v-slot:item.fileSize="{ item }">
              {{ formatBytes(item.fileSize) }}
            </template>
            <template v-slot:item.uploadedAt="{ item }">
              {{ formatDate(item.uploadedAt) }}
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn icon @click="viewDocument(item._id)" title="View" color="primary">
                <v-icon>mdi-eye</v-icon>
              </v-btn>
              <v-btn icon @click="downloadDocument(item._id)" title="Download">
                <v-icon>mdi-download</v-icon>
              </v-btn>
              <v-btn
                v-if="isAdminOrSuperuserOrManager"
                icon
                @click="deleteDocument(item)"
                color="error"
                title="Delete"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Dialog de Upload -->
    <v-dialog v-model="uploadDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title>
          <span class="text-h5">Upload Document</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="uploadForm">
            <v-text-field
              v-model="friendlyName"
              label="Document Name *"
              required
              outlined
              :rules="[v => !!v || 'Name is required']"
            ></v-text-field>
            
            <v-file-input
              v-model="file"
              label="Select File *"
              outlined
              show-size
              :rules="[v => !!v || 'File is required']"
            ></v-file-input>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeUploadDialog" :disabled="uploading">Cancel</v-btn>
          <v-btn color="primary" @click="uploadDocument" :loading="uploading">Upload</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Histórico de acesso aos documentos -->
    <v-dialog v-model="accessHistoryDialog" max-width="1100px" persistent>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon left>mdi-account-clock</v-icon>
          Access history – who opened or downloaded documents
          <v-spacer></v-spacer>
          <v-btn icon @click="accessHistoryDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row class="mb-3">
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="accessHistoryFilters.userName"
                label="User name"
                placeholder="Email, username, first or last name"
                outlined
                dense
                hide-details
                clearable
                @input="debouncedLoadAccessHistory"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="3">
              <v-select
                v-model="accessHistoryFilters.role"
                :items="accessHistoryRoleOptions"
                label="User type (role)"
                outlined
                dense
                hide-details
                clearable
                @change="loadAccessHistory"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="3">
              <v-text-field
                v-model="accessHistoryFilters.npi"
                label="NPI"
                outlined
                dense
                hide-details
                clearable
                @input="debouncedLoadAccessHistory"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="2" class="d-flex align-center">
              <v-btn color="primary" small @click="loadAccessHistory">Apply</v-btn>
            </v-col>
          </v-row>
          <v-data-table
            :headers="accessHistoryHeaders"
            :items="accessHistoryLogs"
            :loading="loadingAccessHistory"
            class="elevation-0"
            dense
          >
            <template v-slot:item.downloadedAt="{ item }">
              {{ formatDate(item.downloadedAt) }}
            </template>
            <template v-slot:item.downloadedBy="{ item }">
              <template v-if="item.downloadedBy">
                {{ displayName(item.downloadedBy) }}
              </template>
              <span v-else>—</span>
            </template>
            <template v-slot:item.role="{ item }">
              {{ item.downloadedBy && item.downloadedBy.role ? item.downloadedBy.role : '—' }}
            </template>
            <template v-slot:item.npi="{ item }">
              {{ item.downloadedBy && item.downloadedBy.npi ? item.downloadedBy.npi : '—' }}
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Snackbar para mensagens -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000">
      {{ snackbarMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ProviderDocuments',
  data() {
    return {
      documents: [],
      stats: {
        totalDocuments: 0,
        totalSize: 0,
        totalDownloads: 0
      },
      search: '',
      loading: false,
      uploadDialog: false,
      uploading: false,
      friendlyName: '',
      file: null,
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      user: { role: '', insurance: [] },
      accessHistoryLogs: [],
      accessHistoryDialog: false,
      loadingAccessHistory: false,
      accessHistoryFilters: { userName: '', role: '', npi: '' },
      accessHistoryHeaders: [
        { text: 'Document', value: 'documentFriendlyName' },
        { text: 'User (name, username)', value: 'downloadedBy' },
        { text: 'User type', value: 'role' },
        { text: 'NPI', value: 'npi' },
        { text: 'When', value: 'downloadedAt' },
      ],
      headers: [
        { text: 'Document Name', value: 'friendlyName' },
        { text: 'File Size', value: 'fileSize' },
        { text: 'Downloads', value: 'downloadCount' },
        { text: 'Uploaded By', value: 'uploadedBy.username' },
        { text: 'Upload Date', value: 'uploadedAt' },
        { text: 'Actions', value: 'actions', sortable: false },
      ]
    };
  },
  computed: {
    isAdminOrSuperuserOrManager() {
      if (!this.user || !this.user.role) return false;
      const role = String(this.user.role).toLowerCase();
      return role === 'admin' || role === 'superuser' || role === 'manager';
    },
    accessHistoryRoleOptions() {
      const roles = new Set();
      (this.accessHistoryLogs || []).forEach(log => {
        const r = log.downloadedBy && log.downloadedBy.role;
        if (r && String(r).trim()) roles.add(String(r).trim());
      });
      return Array.from(roles).sort();
    }
  },
  mounted() {
    this.getUser();
    this.loadDocuments();
    this.loadStats();
  },
  methods: {
    async getUser() {
      try {
        const response = await axios.get('/api/user');
        this.user = response.data[0];
      } catch (error) {
        console.error('Error loading user:', error);
        this.$router.push('/login');
      }
    },
    async loadDocuments() {
      this.loading = true;
      try {
        const response = await axios.get('/api/provider-documents');
        this.documents = response.data.documents;
      } catch (error) {
        console.error('Error loading documents:', error);
        this.showSnackbar('Error loading documents', 'error');
      } finally {
        this.loading = false;
      }
    },
    async loadStats() {
      try {
        const response = await axios.get('/api/provider-documents/stats/summary');
        this.stats = response.data;
      } catch (error) {
        console.error('Error loading stats:', error);
      }
    },
    openUploadDialog() {
      this.uploadDialog = true;
    },
    closeUploadDialog() {
      this.uploadDialog = false;
      this.friendlyName = '';
      this.file = null;
      if (this.$refs.uploadForm) {
        this.$refs.uploadForm.reset();
      }
    },
    openAccessHistoryDialog() {
      this.accessHistoryDialog = true;
      this.accessHistoryFilters = { userName: '', role: '', npi: '' };
      this.loadAccessHistory();
    },
    loadAccessHistory() {
      if (this._accessHistoryTimer) clearTimeout(this._accessHistoryTimer);
      this._loadAccessHistoryNow();
    },
    debouncedLoadAccessHistory() {
      if (this._accessHistoryTimer) clearTimeout(this._accessHistoryTimer);
      this._accessHistoryTimer = setTimeout(() => this._loadAccessHistoryNow(), 400);
    },
    async _loadAccessHistoryNow() {
      this.loadingAccessHistory = true;
      try {
        const params = new URLSearchParams();
        if (this.accessHistoryFilters.userName) params.set('userName', this.accessHistoryFilters.userName);
        if (this.accessHistoryFilters.role) params.set('role', this.accessHistoryFilters.role);
        if (this.accessHistoryFilters.npi) params.set('npi', this.accessHistoryFilters.npi);
        const qs = params.toString();
        const url = '/api/provider-documents/downloads/logs' + (qs ? '?' + qs : '');
        const response = await axios.get(url);
        this.accessHistoryLogs = response.data.logs || [];
      } catch (error) {
        console.error('Error loading access history:', error);
        this.showSnackbar('Error loading access history', 'error');
        this.accessHistoryLogs = [];
      } finally {
        this.loadingAccessHistory = false;
      }
    },
    async uploadDocument() {
      if (!this.$refs.uploadForm.validate()) {
        return;
      }
      
      this.uploading = true;
      
      try {
        const formData = new FormData();
        formData.append('document', this.file);
        formData.append('friendlyName', this.friendlyName);
        
        await axios.post('/api/provider-documents/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        this.showSnackbar('Document uploaded successfully!', 'success');
        this.closeUploadDialog();
        this.loadDocuments();
        this.loadStats();
        
      } catch (error) {
        console.error('Error uploading document:', error);
        const errorMsg = (error.response && error.response.data && error.response.data.error) || 'Error uploading document';
        this.showSnackbar(errorMsg, 'error');
      } finally {
        this.uploading = false;
      }
    },
    downloadDocument(id) {
      // Navegação direta para o endpoint: o backend redireciona (302) para o Azure,
      // e o navegador segue o redirect e baixa o arquivo (evita CORS com responseType blob).
      const url = `${window.location.origin}/api/provider-documents/${id}/download`;
      window.open(url, '_blank', 'noopener,noreferrer');
      this.loadStats();
      this.loadDocuments();
    },
    async viewDocument(id) {
      try {
        // Encontrar o documento para obter informações
        const document = this.documents.find(doc => doc._id === id);
        
        // Determinar tipo de arquivo
        const fileName = document.fileName.toLowerCase();
        
        if (fileName.endsWith('.pdf')) {
          // Para PDFs, abrir diretamente no navegador
          window.open(`/api/provider-documents/${id}/download`, '_blank');
        } else if (fileName.endsWith('.txt')) {
          // Para arquivos de texto, abrir em nova aba
          window.open(`/api/provider-documents/${id}/download`, '_blank');
        } else if (fileName.endsWith('.jpg') || fileName.endsWith('.jpeg') || fileName.endsWith('.png')) {
          // Para imagens, abrir diretamente
          window.open(`/api/provider-documents/${id}/download`, '_blank');
        } else {
          // Para outros tipos, tentar abrir no navegador
          window.open(`/api/provider-documents/${id}/download`, '_blank');
        }
        
        this.showSnackbar('Document opened in new tab', 'success');
        
      } catch (error) {
        console.error('Error viewing document:', error);
        this.showSnackbar('Error viewing document', 'error');
      }
    },
    async deleteDocument(item) {
      if (!confirm(`Delete document "${item.friendlyName}"?`)) {
        return;
      }
      
      try {
        await axios.delete(`/api/provider-documents/${item._id}`);
        this.showSnackbar('Document deleted successfully', 'success');
        this.loadDocuments();
        this.loadStats();
      } catch (error) {
        console.error('Error deleting document:', error);
        this.showSnackbar('Error deleting document', 'error');
      }
    },
    formatBytes(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    },
    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      return d.toLocaleDateString('en-US') + ' ' + d.toLocaleTimeString('en-US');
    },
    displayName(downloadedBy) {
      if (!downloadedBy) return '—';
      const firstLast = [downloadedBy.firstName, downloadedBy.lastName].filter(Boolean).join(' ').trim();
      if (firstLast) return firstLast;
      return downloadedBy.username || downloadedBy.email || '—';
    },
    showSnackbar(message, color = 'success') {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.snackbar = true;
    }
  }
};
</script>

<style scoped>
.text-h4 {
  font-weight: 600;
  color: #1976d2;
}
</style>

